import { Backpack, CalendarDots, Signpost, Ticket, Trash, Umbrella } from '@phosphor-icons/react';
import type { Hint } from '@shh/shared';
import { Icon, cn } from '@shh/ui';
import { Link } from 'react-router';
import { todayText } from '../i18n/today';

const ICON = {
  bring: Backpack,
  rain: Umbrella,
  waste: Trash,
  tomorrow: CalendarDots,
  voucher: Ticket,
  idea: Signpost,
} as const;

/**
 * "Denkt mit": short hints from the family's own data. Quiet on "Heute" (one line each),
 * large on the resting wall display. Nothing to say → nothing shown. Freizeit hints lead to
 * their list when tapped (not on the resting wall – a tap there only wakes it).
 */
export function Hints({ hints, size = 'md' }: { hints: Hint[]; size?: 'md' | 'lg' }) {
  if (!hints.length) return null;
  return (
    <section aria-label={todayText.goodToKnow}>
      <ul className={cn('flex flex-col', size === 'lg' ? 'gap-gap-md' : 'gap-gap-xs')}>
        {hints.map((h) => {
          const content = (
            <>
              <Icon
                icon={ICON[h.kind]}
                size={size === 'lg' ? 'lg' : 'sm'}
                weight="duotone"
                className={cn('shrink-0', size === 'md' && 'text-accent-primary')}
              />
              <span className={cn(h.href && size === 'md' && 'underline underline-offset-2')}>
                {h.text}
              </span>
            </>
          );
          const line = cn(
            'flex items-start gap-gap-sm',
            size === 'lg' ? 'text-lg font-semibold' : 'text-sm font-semibold text-text-secondary',
          );
          return (
            <li key={h.id}>
              {h.href && size === 'md' ? (
                <Link
                  to={h.href}
                  className={cn(
                    line,
                    'rounded-control outline-none hover:text-text-primary focus-visible:outline-3 focus-visible:outline-border-focus',
                  )}
                >
                  {content}
                </Link>
              ) : (
                <div className={line}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
